// Copia al CRM de SuperAdmin V2 (INVU) el lead que el form de Clau manda a
// HubSpot, marcado como Clau. Va en paralelo a HubSpot y nunca bloquea ni
// rompe el envío del form.

const CRM_LEADS_URL = "https://super.invupos.com/api/public/crm/leads";

// Header secret del webhook público de leads de V2. Solo sirve para crear
// leads y el WAF limita las solicitudes por IP.
const CRM_WEBHOOK_SECRET =
  "f674900ea4c5c7a71353e357183a91e021293b469b41e1b24e3ca7820f475765";

// Distribuidor de Billing que recibe el lead (y le da su país en el CRM).
// Cada llave es sha256("crm-lead-express-distributor:v1:<id>"), la que valida V2.
const INVU_POS_PANAMA =
  "3566da930ce16a7bb6c8d2cca9301d0885735c7cb19426e4ae10c547a5f4c235"; // 1
const DISTRIBUTOR_KEY_BY_COUNTRY = {
  panama: INVU_POS_PANAMA,
  venezuela:
    "82118e96038a12d645e9a8aa123d29bcc7672e73e21b7ee78be9378701a7e072", // 2
  elsalvador:
    "8077ab4e2452757155c83630e441b49effa087b40b0227578f9411c424f44e84", // 5
  republicadominicana:
    "ad43ab340a09bd22a25492f4a4b8ec964fc299bd3c899f64cb51cd52e0741627", // 9
  guatemala:
    "dae8e543a13e69556e145a3e689b99e1d8a598040346b35b86995efc5485f11d", // 10
  mexico:
    "8c5ee28f14b8998c32a7fb2f9b7aa4ce67194c749d3cc6a49b9e5f2a43622ccd", // 3
  colombia:
    "39764c1967cf19ae8a411ce7bd25cf8737f74ef702c6e6fd9c5e85971f675dc3", // 8
  peru:
    "3e6ba80bad50d8f5acee4dd93405261ab08502b7eff8e8be8751cbe419de321c", // 11
};

// El sitio no pregunta el país: sale de la zona horaria del navegador.
const COUNTRY_BY_TIMEZONE = {
  "America/Panama": "panama",
  "America/Caracas": "venezuela",
  "America/El_Salvador": "elsalvador",
  "America/Santo_Domingo": "republicadominicana",
  "America/Guatemala": "guatemala",
  "America/Costa_Rica": "costarica",
  "America/Bogota": "colombia",
  "America/Lima": "peru",
  "America/Santiago": "chile",
  "America/Port_of_Spain": "trinidadytobago",
  "America/Mexico_City": "mexico",
  "America/Cancun": "mexico",
  "America/Monterrey": "mexico",
  "America/Merida": "mexico",
  "America/Tijuana": "mexico",
};

const COUNTRY_NAMES = {
  panama: "Panamá",
  mexico: "México",
  venezuela: "Venezuela",
  elsalvador: "El Salvador",
  costarica: "Costa Rica",
  colombia: "Colombia",
  guatemala: "Guatemala",
  republicadominicana: "República Dominicana",
  trinidadytobago: "Trinidad y Tobago",
  peru: "Perú",
  chile: "Chile",
};

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

function resolveCountry() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return COUNTRY_BY_TIMEZONE[timeZone] || null;
  } catch {
    return null;
  }
}

function clean(value, max) {
  const text = String(value || "").replace(CONTROL_CHARS, "").trim();
  return text ? text.slice(0, max) : null;
}

function cleanEmail(value) {
  const email = clean(value, 254);
  return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function cleanPhone(value) {
  const phone = clean(value, 50);
  if (!phone || !/^[+0-9().\-# xX]+$/.test(phone)) return null;
  const digits = phone.replace(/\D/g, "").length;
  return digits >= 3 && digits <= 20 ? phone : null;
}

function newRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

function post(body) {
  return fetch(CRM_LEADS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-crm-webhook-secret": CRM_WEBHOOK_SECRET,
    },
    body,
  });
}

// Reintenta una vez (mismo cuerpo) si falla la red o V2 responde alguno de
// los estados de `retryOn`.
function send(body, retryOn) {
  post(body)
    .catch(() => null)
    .then((response) =>
      response && !retryOn(response.status)
        ? response
        : new Promise((resolve) => setTimeout(resolve, 3000)).then(() =>
            post(body)
          )
    )
    .then((response) => {
      if (!response.ok) {
        console.warn("CRM: solicitud no aceptada", response.status);
      }
    })
    .catch((error) => console.warn("CRM: solicitud no enviada", error));
}

export default function sendLeadToCrm({
  fullName,
  email,
  phone,
  company,
  message,
  formName,
}) {
  try {
    const countryId = resolveCountry();
    const name = clean(fullName, 241) || "";
    const [first, ...rest] = name.split(/\s+/);
    const contact = {
      firstName: clean(first, 120),
      lastName: clean(rest.join(" "), 120),
      email: cleanEmail(email),
      phone: cleanPhone(phone),
    };
    const legalName = clean(company, 255) || clean(name || contact.email, 255);
    if (!legalName || (!contact.firstName && !contact.email)) return null;

    const description = [
      "Producto: Clau (clau.io)",
      `Formulario: ${formName}`,
      countryId ? `País: ${COUNTRY_NAMES[countryId]}` : null,
      `Página: ${window.location.href}`,
      clean(message, 1500) ? `Mensaje: ${clean(message, 1500)}` : null,
    ]
      .filter(Boolean)
      .join("\n")
      .slice(0, 2000);

    // El mismo requestId hace idempotente el reintento en V2 y es la llave
    // con la que el paso 2 agrega sus respuestas al lead.
    const requestId = newRequestId();
    const body = JSON.stringify({
      requestId,
      distributorKey: DISTRIBUTOR_KEY_BY_COUNTRY[countryId] || INVU_POS_PANAMA,
      company: { legalName },
      contact,
      lead: {
        origin: "web",
        title: `Clau · ${legalName}`.slice(0, 255),
        description,
      },
    });

    send(body, (status) => status >= 500);
    return requestId;
  } catch (error) {
    console.warn("CRM: lead no enviado", error);
    return null;
  }
}

const QUALIFICATION_SUBTYPE_LABELS = {
  tipo_de_restaurante: "Tipo de restaurante",
  tipo_de_minorista: "Tipo de minorista",
  tipo_entretenimiento: "Tipo de entretenimiento",
  tipo_de_hospitalidad_y_turismo: "Tipo de hospitalidad y turismo",
  tipo_de_servicios_profesionales: "Tipo de servicios profesionales",
};

// Paso 2 (Demo_confirmed): agrega las respuestas al lead que creó el paso 1,
// identificado por el requestId que devolvió sendLeadToCrm.
export function sendLeadQualificationToCrm(
  requestId,
  {
    lookingFor,
    industry,
    subtypeName,
    subtype,
    employees,
    interestedIn,
    occupation,
  }
) {
  try {
    if (!requestId) return;
    const qualification = [
      ["Estoy buscando", lookingFor],
      ["Tipo de industria", industry],
      [QUALIFICATION_SUBTYPE_LABELS[subtypeName] || "Subtipo", subtype],
      ["Cantidad de colaboradores", employees],
      ["Estoy interesado en", interestedIn],
      ["Ocupación", occupation],
    ]
      .map(([label, value]) => ({ label, value: clean(value, 500) }))
      .filter((answer) => answer.value);
    if (qualification.length === 0) return;

    // 404/409: el lead del paso 1 todavía se está creando.
    send(
      JSON.stringify({ requestId, qualification }),
      (status) => status === 404 || status === 409 || status >= 500
    );
  } catch (error) {
    console.warn("CRM: calificación no enviada", error);
  }
}
